import React from 'react';
import { Link } from 'react-router-dom';
import { Tag, Space } from 'antd';

import {
  ApiOutlined,
  BarChartOutlined,
  BulbOutlined,
  DashboardOutlined,
  GlobalOutlined,
  HistoryOutlined,
  PieChartOutlined,
  CloudOutlined,
  FileSyncOutlined,
  CheckSquareOutlined,
  CreditCardOutlined,
  SettingOutlined,
  StockOutlined,
  LaptopOutlined,
  LockOutlined,
  EyeOutlined,
  ThunderboltOutlined,
  DollarCircleOutlined,
} from '@ant-design/icons';

export enum PageKey {
  Integrations = 'integrations',
  Billing = 'billing',
  Security = 'security',
  Settings = 'settings',
  IntegrationWizard = 'integration-wizard',
  DashboardV3 = 'dashboard-v3',
  AIInsights = 'ai-insights',
  DomainAnalytics = 'domain-analytics',
  DeepInsight = 'deep-insight',
  CacheManager = 'cache-manager',
  DomainManager = 'domain-manager',
  Sitemaps = 'sitemaps',
  ScheduledRendering = 'scheduled-rendering',
  RenderHistory = 'render-history',
  Domain404Checker = 'domain-404-checker',
  Oversight = 'oversight',
  CustomDeal = 'custom-deal',
  Qa = 'qa',
  CDNAnalyticsV2 = 'cdn-analytics-v2',
  SearchPerformance = 'search-performance',
}

// label: <LinkWithTag to="/page-url">test</LinkWithTag>;
type LinkWithTagProps = {
  to: string;
  tag?: string;
  color?: string;
};
function LinkWithTag({ to, children, tag = 'New', color = 'purple' }: React.PropsWithChildren<LinkWithTagProps>) {
  return (
    <Link to={to}>
      <Space size="small">
        <span>{children}</span>
        <Tag color={color}>{tag}</Tag>
      </Space>
    </Link>
  );
}

export type LeafMenuItem = {
  type: 'item';
  id: PageKey;
  target: string;
  visible: boolean;
  label: React.ReactNode;
  icon: React.ReactNode;
  extra?: React.ReactNode;
  order: number;
};
export type GroupMenuItem = {
  type: 'group';
  id: string;
  label: string;
  children: LeafMenuItem[];
  extra?: React.ReactNode;
  order: number;
};
export type MenuItem = LeafMenuItem | GroupMenuItem;

const settingsItems: LeafMenuItem[] = [
  {
    type: 'item',
    id: PageKey.Integrations,
    label: <Link to="/integrations">Integrations</Link>,
    icon: <ApiOutlined />,
    target: '/integrations',
    visible: true,
    order: 0,
  },
  {
    type: 'item',
    id: PageKey.Billing,
    label: <Link to="/billing">Billing</Link>,
    icon: <CreditCardOutlined />,
    target: '/billing',
    visible: true,
    order: 1,
  },
  {
    type: 'item',
    id: PageKey.Security,
    label: <Link to="/security">Security & Access</Link>,
    icon: <LockOutlined />,
    target: '/security',
    visible: true,
    order: 2,
  },
  {
    id: PageKey.Settings,
    type: 'item',
    label: <Link to="/settings">Advanced Settings</Link>,
    icon: <SettingOutlined />,
    target: '/settings',
    visible: true,
    order: 3,
  },
];

const menuItems: MenuItem[] = [
  {
    type: 'item',
    id: PageKey.IntegrationWizard,
    label: <Link to="/integration-wizard">Get Started</Link>,
    icon: <ThunderboltOutlined />,
    target: '/integration-wizard',
    visible: false,
    order: 0,
  },
  {
    type: 'item',
    id: PageKey.DashboardV3,
    label: <Link to="/">Dashboard</Link>,
    icon: <DashboardOutlined />,
    target: '/',
    visible: true,
    order: 1,
  },
  {
    type: 'item',
    id: PageKey.AIInsights,
    label: (
      <LinkWithTag to="/ai-insights" color="green">
        AI Insights
      </LinkWithTag>
    ),
    icon: <BulbOutlined />,
    target: '/ai-insights',
    visible: false,
    order: 1.2,
  },
  {
    type: 'item',
    id: PageKey.SearchPerformance,
    label: (
      <LinkWithTag to="/search-performance" tag="Beta" color="purple">
        Search Performance
      </LinkWithTag>
    ),
    icon: <BarChartOutlined />,
    target: '/search-performance',
    visible: true,
    order: 1.5,
  },
  {
    type: 'item',
    id: PageKey.CDNAnalyticsV2,
    label: <Link to="/cdn-analytics">CDN Analytics</Link>,
    icon: <GlobalOutlined />,
    target: '/cdn-analytics',
    visible: true,
    order: 2,
  },
  {
    type: 'item',
    id: PageKey.DeepInsight,
    label: <Link to="/insight">Deep Insight</Link>,
    icon: <PieChartOutlined />,
    target: '/insight',
    visible: true,
    order: 3,
  },
  {
    type: 'item',
    id: PageKey.CacheManager,
    label: <Link to="/cache">Cache Manager</Link>,
    icon: <CloudOutlined />,
    target: '/cache',
    visible: true,
    order: 4,
  },
  {
    type: 'item',
    id: PageKey.DomainManager,
    label: <Link to="/domain-manager">Domain Manager</Link>,
    icon: <LaptopOutlined />,
    target: '/domain-manager',
    visible: true,
    order: 5,
  },
  {
    type: 'item',
    id: PageKey.Sitemaps,
    label: <Link to="/sitemaps">Sitemaps</Link>,
    icon: <FileSyncOutlined />,
    target: '/sitemaps',
    visible: true,
    order: 6,
  },
  {
    type: 'item',
    id: PageKey.ScheduledRendering,
    label: <Link to="/recache-queue">Caching Queue</Link>,
    icon: <HistoryOutlined />,
    target: '/recache-queue',
    visible: true,
    order: 7,
  },
  {
    type: 'item',
    id: PageKey.RenderHistory,
    label: <Link to="/recache-stats">Render History</Link>,
    icon: <StockOutlined />,
    target: '/recache-stats',
    visible: true,
    order: 8,
  },
  {
    type: 'item',
    id: PageKey.Domain404Checker,
    label: <Link to="/domain-404-checker">404 Checker</Link>,
    icon: <CheckSquareOutlined />,
    target: '/domain-404-checker',
    visible: true,
    order: 9,
  },
  {
    type: 'item',
    id: PageKey.Oversight,
    label: <Link to="/oversight">Oversight</Link>,
    icon: <EyeOutlined />,
    target: '/oversight',
    visible: false,
    order: 10,
  },
  {
    type: 'item',
    id: PageKey.CustomDeal,
    label: <Link to="/custom-deal">Custom deal</Link>,
    icon: <DollarCircleOutlined />,
    target: '/custom-deal',
    visible: true,
    order: 12,
  },
  // {
  //   id: 'qa',
  //   label: 'Quality Assurance',
  //   icon: <EyeOutlined />,
  //   target: '/qa',
  //   group: 'Experimental Features',
  //   visible: true,
  // },
  {
    type: 'group',
    id: 'settings',
    label: 'Settings',
    children: settingsItems,
    order: 13,
  },
];

export default menuItems;
